<template>
  <b-card
    no-body
    class="inspection-question-item"
    :class="{
      'inspection-question-item--invalid': questionInvalid,
    }"
  >
    <div v-if="!viewOnly" class="inspection-question-item__drag-handle">
      <McIconTooltip
        :id="`question-drag-handle-${question.key}`"
        :data-me="`setting-vehicle-inspection-question-drag-${question.key}`"
        :tooltip="$t('vehicleInspection.toolTip.drag')"
        :force-show="forceShow"
        icon="icon-outline_drag-vertical"
        :icon-size="20"
        variant="secondary"
        placement="bottom"
      />
    </div>
    <b-row no-gutters>
      <b-col cols="12" :md="viewOnly ? 12 : true">
        <b-row no-gutters align-v="center" class="inspection-question-item__header">
          <b-col cols="auto" class="inspection-question-item__number"> {{ index + 1 }}. </b-col>
          <b-col>
            <FormulateInput
              v-if="!viewOnly"
              ref="questionNameInput"
              v-model="questionName"
              :data-me="`setting-vehicle-inspection-question-input-name-${question.key}`"
              class="inspection-question-item__title"
              :placeholder="titlePlaceholder"
              maxlength="128"
              underline
            />
            <div v-else class="inspection-question-item__title">
              {{ question.name || titlePlaceholder }}
              <span v-if="question.isRequired" style="color: red">&ast;</span>
            </div>
          </b-col>
        </b-row>

        <div>
          <b-row no-gutters align-v="center" class="inspection-question-item__type">
            <b-col cols="auto">
              <span class="inspection-question-item__type-label">{{
                $t('vehicleInspection.form.answerFormat')
              }}</span>
            </b-col>
            <b-col cols="auto">
              <div class="inspection-question-item__type-options" role="radiogroup">
                <button
                  v-for="option in QUESTION_TYPE_OPTIONS"
                  :key="option.value"
                  :data-me="`setting-vehicle-inspection-question-btn-answer-format-${option.value}-${question.key}`"
                  type="button"
                  role="radio"
                  :class="[
                    'inspection-question-item__type-option',
                    {
                      'inspection-question-item__type-option--selected':
                        answerFormat === option.value,
                    },
                  ]"
                  :aria-checked="answerFormat === option.value"
                  :disabled="viewOnly"
                  @click="answerFormat = option.value"
                >
                  <span class="inspection-question-item__type-icon" :class="option.icon"></span>
                  <span>{{ $t(option.label) }}</span>
                </button>
              </div>
            </b-col>
          </b-row>

          <div
            v-if="question.type === QUESTION_TYPES.options"
            class="inspection-question-item__answers"
          >
            <b-row>
              <b-col
                v-for="(option, optionIndex) in question.options"
                :key="option.value"
                cols="12"
                md="6"
                :class="[
                  'inspection-question-item__answer-option',
                  { 'inspection-question-item__answer-option--healthy': option.value },
                ]"
              >
                <FormulateInput
                  type="text"
                  :data-me="`setting-vehicle-inspection-question-input-answer-${option.value}-${question.key}`"
                  ignored
                  underline
                  :formulate-value="option.label"
                  :variant="option.value ? 'success' : 'danger'"
                  :disabled="viewOnly"
                  :placeholder="`${$t('vehicleInspection.fields.question')} ${optionIndex + 1}`"
                  maxlength="32"
                  @input="handleLabelOptionUpdate(option.value, $event)"
                />

                <span v-if="option.value" class="inspection-question-item__healthy-status">
                  {{ $t('vehicleInspection.form.healthyCondition') }}
                  <i class="icon-fill_checkmark-circle-2" />
                </span>
              </b-col>
            </b-row>
            <small class="inspection-question-item__answer-help">
              {{ $t('vehicleInspection.form.answerHelper') }}
            </small>
          </div>

          <div
            v-if="question.type === QUESTION_TYPES.text"
            class="inspection-question-item__answers"
          >
            <h5>{{ $t('vehicleInspection.fields.text') }}</h5>
            <b-form-textarea
              :value="question.placeholder"
              rows="2"
              :placeholder="$t('vehicleInspection.placeholder.questionText')"
              disabled
            />
          </div>
        </div>
        <section class="inspection-question-item__supportive-evidence">
          <h5>{{ $t('vehicleInspection.form.supportiveEvidence') }}</h5>
          <b-row class="inspection-question-item__evidence-options">
            <b-col
              v-if="question.type === QUESTION_TYPES.options"
              cols="auto"
              class="inspection-question-item__evidence-option d-flex align-items-center gap-2"
            >
              <McCheckbox
                :id="`note-evidence-${question.key}`"
                v-model="noteEnabled"
                :data-me="`setting-vehicle-inspection-question-toggle-note-${question.key}`"
                :label="`${$t('vehicleInspection.form.note')} `"
                :disabled="viewOnly"
              />
              <FormulateInput
                v-if="noteEnabled"
                v-model="notePolicyValue"
                :data-me="`setting-vehicle-inspection-question-input-note-policy-${question.key}`"
                type="select-resourceful"
                trackBy="value"
                :placeholder="$t('vehicleInspection.placeholder.selectPolicy')"
                :optionsGetter="getSupportiveEvidencePolicyOptions"
                :optionLabelFn="supportiveEvidencePolicyLabel"
                :initialObject="getSupportiveEvidencePolicyOption(notePolicyValue)"
                :useInitialFallback="true"
                :disabled="viewOnly"
                :allow-empty="false"
              />
            </b-col>
            <b-col
              cols="auto"
              class="inspection-question-item__evidence-option d-flex align-items-center gap-2"
            >
              <McCheckbox
                :id="`photo-evidence-${question.key}`"
                v-model="photoEnabled"
                :data-me="`setting-vehicle-inspection-question-toggle-photo-${question.key}`"
                :label="$t('vehicleInspection.form.photo')"
                :disabled="viewOnly"
              />
              <FormulateInput
                v-if="photoEnabled"
                v-model="photoPolicyValue"
                :data-me="`setting-vehicle-inspection-question-input-photo-policy-${question.key}`"
                type="select-resourceful"
                trackBy="value"
                :placeholder="$t('vehicleInspection.placeholder.selectPolicy')"
                :optionsGetter="getSupportiveEvidencePolicyOptions"
                :optionLabelFn="supportiveEvidencePolicyLabel"
                :initialObject="getSupportiveEvidencePolicyOption(photoPolicyValue)"
                :useInitialFallback="true"
                :disabled="viewOnly"
                :allow-empty="false"
              />
            </b-col>
          </b-row>
        </section>
      </b-col>

      <b-col v-if="!viewOnly" cols="12" md="auto" class="mt-2 mt-md-0">
        <div
          class="inspection-question-item__actions d-flex align-items-center justify-content-end gap-4"
        >
          <FormulateInput
            v-model="questionIsRequired"
            :data-me="`setting-vehicle-inspection-question-toggle-required-${question.key}`"
            class="inspection-question-item__critical-switch"
            type="switch"
            :valueLabelTrue="requiredLabel"
            :valueLabelFalse="requiredLabel"
          />
          <button
            v-b-tooltip.hover="
              `${$t('global.duplicate')} ${$t('vehicleInspection.fields.question')}`
            "
            :data-me="`setting-vehicle-inspection-question-btn-duplicate-${question.key}`"
            type="button"
            class="inspection-question-item__icon-button"
            @click="$emit('duplicate')"
          >
            <i class="mc-icon icon-outline_copy"></i>
          </button>
          <button
            v-b-tooltip.hover="`${$t('global.delete')} ${$t('vehicleInspection.fields.question')}`"
            :data-me="`setting-vehicle-inspection-question-btn-delete-${question.key}`"
            type="button"
            class="inspection-question-item__icon-button inspection-question-item__icon-button--danger"
            :disabled="!canRemove"
            @click="$emit('delete')"
          >
            <i class="mc-icon icon-outline_trash"></i>
          </button>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import McIconTooltip from '@/components/McIconTooltip'
import McCheckbox from '@/components/McCheckbox'

import {
  DEFAULT_ANSWER_OPTIONS,
  QUESTION_TYPES,
  QUESTION_TYPE_OPTIONS,
  SUPPORTIVE_EVIDENCE_POLICIES,
  SUPPORTIVE_EVIDENCE_POLICY_OPTIONS,
} from '../../lib/constants'

export default {
  components: {
    McIconTooltip,
    McCheckbox,
  },
  props: {
    question: {
      type: Object,
      default: undefined,
    },
    index: {
      type: Number,
      required: true,
    },
    questionInvalid: {
      type: Boolean,
      default: false,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
    forceShow: {
      type: Boolean,
      default: false,
    },
    shouldFocusName: {
      type: Boolean,
      default: false,
    },
    canRemove: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update', 'delete', 'duplicate'],
  data() {
    return {
      QUESTION_TYPES,
      QUESTION_TYPE_OPTIONS,
    }
  },
  computed: {
    titlePlaceholder() {
      return this.$t('vehicleInspection.placeholder.questionItem')
    },
    requiredLabel() {
      return this.$t('vehicleInspection.form.isRequired')
    },
    questionName: {
      get() {
        return this.question.name
      },
      set(value) {
        this.handleFieldUpdate('name', value)
      },
    },
    questionIsRequired: {
      get() {
        return this.question.isRequired
      },
      set(value) {
        this.handleFieldUpdate('isRequired', value)
      },
    },
    noteEnabled: {
      get() {
        return this.question.isNoteEnabled
      },
      set(value) {
        this.handleEvidenceEnabledChange('note', value)
      },
    },
    photoEnabled: {
      get() {
        return this.question.isPhotoEnabled
      },
      set(value) {
        this.handleEvidenceEnabledChange('photo', value)
      },
    },
    notePolicyValue: {
      get() {
        return this.question.notePolicy
      },
      set(value) {
        this.handleFieldUpdate('notePolicy', value)
      },
    },
    photoPolicyValue: {
      get() {
        return this.question.photoPolicy
      },
      set(value) {
        this.handleFieldUpdate('photoPolicy', value)
      },
    },
    answerFormat: {
      get() {
        return this.question.type
      },
      set(type) {
        this.handleFormatChange(type)
      },
    },
  },
  watch: {
    shouldFocusName(shouldFocusName) {
      if (shouldFocusName) this.focusNameInput()
    },
  },
  mounted() {
    if (this.shouldFocusName) this.focusNameInput()
  },
  methods: {
    focusNameInput() {
      this.$nextTick(() => {
        this.$refs.questionNameInput?.$el.querySelector('input')?.focus()
      })
    },
    handleFormatChange(type) {
      const isOptions = type === QUESTION_TYPES.options

      const options = this.question.options?.length
        ? this.question.options
        : DEFAULT_ANSWER_OPTIONS.map((option) => ({
            ...option,
            label: this.$t(option.label),
          }))

      const fields = { type, options }

      if (!isOptions) {
        fields.isNoteEnabled = false
        fields.notePolicy = null

        if (this.question.photoPolicy === SUPPORTIVE_EVIDENCE_POLICIES.requiredOnUnhealthy) {
          fields.photoPolicy = SUPPORTIVE_EVIDENCE_POLICIES.optional
        }
      }

      this.handleFieldBatchUpdate(fields)
    },
    handleLabelOptionUpdate(value, text) {
      this.$emit('update', {
        ...this.question,
        options: this.question.options.map((option) =>
          option.value === value ? { ...option, label: text } : option
        ),
      })
    },
    handleEvidenceEnabledChange(type, enabled) {
      const isNote = type === 'note'
      const policyKey = isNote ? 'notePolicy' : 'photoPolicy'

      this.handleFieldBatchUpdate({
        [isNote ? 'isNoteEnabled' : 'isPhotoEnabled']: enabled,
        [policyKey]:
          enabled && !this.question[policyKey]
            ? SUPPORTIVE_EVIDENCE_POLICIES.optional
            : this.question[policyKey],
      })
    },
    getSupportiveEvidencePolicyOption(value) {
      return SUPPORTIVE_EVIDENCE_POLICY_OPTIONS.find((option) => option.value === value)
    },
    supportiveEvidencePolicyLabel({ item }) {
      return this.$t(item.label)
    },
    async getSupportiveEvidencePolicyOptions() {
      let supportiveEvidencePolicyOptions = SUPPORTIVE_EVIDENCE_POLICY_OPTIONS

      if (this.question.type === QUESTION_TYPES.text) {
        supportiveEvidencePolicyOptions = SUPPORTIVE_EVIDENCE_POLICY_OPTIONS.filter(
          (option) => option.value !== SUPPORTIVE_EVIDENCE_POLICIES.requiredOnUnhealthy
        )
      }

      return {
        data: { paginated_result: supportiveEvidencePolicyOptions },
        metadata: { page: 1, total_count: supportiveEvidencePolicyOptions.length },
        isPagination: false,
      }
    },
    handleFieldUpdate(key, value) {
      this.$emit('update', {
        ...this.question,
        [key]: value,
      })
    },
    handleFieldBatchUpdate(fields) {
      this.$emit('update', {
        ...this.question,
        ...fields,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.inspection-question-item {
  position: relative;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  padding: 40px 12px 12px;
  transition: padding-top 150ms ease;

  &--invalid {
    border: 1px solid $red-300;
  }
  &__number {
    font-weight: $font-weight-bold;
    color: #5f6f85;
  }

  &__drag-handle {
    position: absolute;
    top: 12px;
    left: 50%;
    z-index: 1;
    color: #5f6f85;
    width: 40px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transform: translateX(-50%);
    transition: opacity 150ms ease;

    ::v-deep .mc-icon-tooltip {
      margin-right: 0;
    }

    ::v-deep .mc-icon-tooltip__trigger {
      cursor: grab;
    }

    ::v-deep .mc-icon-tooltip__trigger:active {
      cursor: grabbing;
    }
  }

  &__title {
    margin-bottom: 0;

    ::v-deep input {
      border: 0;
      border-bottom: 1px solid $mep-primary-200;
      border-radius: 0;
      box-shadow: none;
      padding-inline: 12px;

      &:focus {
        box-shadow: none;
      }
    }
  }

  &__actions {
    ::v-deep .custom-control {
      margin: 0;
    }

    ::v-deep .custom-control-label {
      color: $mep-color-text-400;
      font-size: 12px;
    }
  }

  &__critical-switch {
    ::v-deep .formulate-input-element--switch > div {
      gap: 4px !important;
      font-size: 11px;
    }

    ::v-deep .vue-switcher {
      transform: scale(0.8);
      transform-origin: left center;
    }
  }

  &__icon-button {
    border: 0;
    color: #5f6f85;
    background: $white;
    border-radius: 6px;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover {
      background: #f6f8fb;
    }

    &--danger {
      color: #e25454;
      background: #ffedeb;
      &:hover {
        background: #ffedeb;
      }
    }
  }

  &__type {
    margin-top: 10px;
  }

  &__type-options {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__type-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: $fw-medium;
    font-size: 16px;
  }

  &__type-label {
    font-size: 14px;
    font-weight: $fw-medium;
    color: #1f2733;
  }

  &__type-option {
    border: 2px solid #c8d0dd;
    background: $white;
    border-radius: $border-radius-pill;
    padding: 3px 14px;
    color: #3f4b63;
    margin: 0;
    font-size: 14px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &--selected {
      border-color: #9fc4f8;
      color: #2b73cf;
      background: #f0f6ff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  @media (max-width: 768px) {
    &__type-label {
      font-size: $font-size-base;
    }

    &__type-option {
      min-height: 34px;
      font-size: $font-size-base;
    }
  }

  &__answers {
    margin-block: 10px;
  }

  &__supportive-evidence {
    border-top: 1px solid #e6ebf2;
    margin-top: 16px;
    padding-top: 12px;

    h5 {
      font-size: 14px;
      font-weight: $font-weight-bold;
      margin-bottom: 10px;
    }
  }

  &__evidence-options {
    margin-bottom: -8px;
  }

  &__evidence-option {
    margin-bottom: 8px;

    ::v-deep .custom-checkbox {
      margin-bottom: 0;
    }

    ::v-deep .custom-control-label {
      font-size: 14px;
    }

    ::v-deep .formulate-input {
      margin-bottom: 0;
      min-width: 300px;
    }

    ::v-deep .formulate-input-element--select-resourceful .multiselect__content-wrapper {
      min-height: auto;
    }
  }

  &__answer-option--healthy {
    ::v-deep .formulate-input-element--text > input {
      padding-right: 150px !important;
    }
  }

  &__answer-option {
    position: relative;
  }

  &__healthy-status {
    position: absolute;
    top: 50%;
    right: 20px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $green;
    font-size: 12px;
    pointer-events: none;
    transform: translateY(-50%);
  }

  &__answer-help {
    display: block;
    margin-top: 6px;
    color: $mep-color-text-300;
  }

  &__checkboxes,
  &__radio-button {
    ::v-deep .custom-control-label {
      padding-left: 5px;
      margin: 0;
    }
  }
}
</style>
